/**
 * This is a generic video player based on Vidstack.
 */

import { h } from 'minidoc-editor';
import { ajax } from 'client/lib/ajax';
import { isVideo } from 'shared/media';
import { type Props as VidstackProps, tagName as vidstackTagName } from './vidstack-web-component';

interface MediaRec {
  url: string;
  poster?: string;
  type: string;
  ratio?: number;
  width?: number;
  captionsURL?: string;
  // Indicates whether or not this is a URL that needs
  // to get resolved via our files endpoint.
  isResolved?: boolean;
  class?: string;
}

export interface Props extends MediaRec {
  fileId?: string;
  hasCaptions?: boolean;
  load?: VidstackProps['load'];
  setProps?(fn: (r: MediaRec) => MediaRec): void;
  tmpUrl?: string;
  captionsURL?: string;
  hideCaptions?: boolean;
  onVideoElement?(el: HTMLVideoElement): void;
  overlay?: string;
  // When true, render a lightweight facade and only mount the real (heavy)
  // Vidstack player once the viewer clicks to play. This keeps a media-heavy
  // lesson from mounting a player per file up front, which freezes the
  // browser (see #6978 for video, #7066 for audio).
  lazy?: boolean;
}

/**
 * Compute the aspect ratio.
 */
export function getAspectRatio(el: HTMLElement) {
  return (el.offsetHeight / el.offsetWidth) * 100;
}

export function getAspectRatioStyle(state: { ratio?: number; width?: number }) {
  if (!state.ratio) {
    return '';
  }
  const ratio = `aspect-ratio: ${state.ratio ? `100 / ${state.ratio};` : '16/9'}`;
  // This complexity is required so that tall images don't get scrunched
  // by our max-height.
  const width = state.width
    ? `width: min(${state.width}px, 100%, calc(var(--max-height, 100vh - 10rem) / ${
        state.ratio / 100
      }));`
    : '';

  return ratio + width;
}

function showMediaError(err: any, placeholder: HTMLElement) {
  console.error(err);
  placeholder.append(
    h(
      '.media-err.grow.h-full.flex.items-center.justify-center.bg-gray-100.text-gray-800.dark:bg-gray-800.rounded-md',
      'Failed to load media.',
    ),
  );
}

const isAbsoluteUrl = (s: string) => /^https?:\/\//.test(s);

// Matches the <IcoPlay /> the real player shows over its poster / in its controls.
const PLAY_ICON_PATH =
  '<path fill-rule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clip-rule="evenodd" />';

function playIcon(size: string) {
  return `<svg viewBox="0 0 24 24" fill="currentColor" class="${size}" aria-hidden="true">${PLAY_ICON_PATH}</svg>`;
}

/**
 * True when this video is the target of a `?focusedFile=` deep link (e.g. a
 * transcript-search result linking to a timestamp). Such a video must mount
 * eagerly so the player can scroll to it and seek/play, so we skip the facade.
 */
function isFocusedFile(props: Props) {
  const focused = new URLSearchParams(location.search).get('focusedFile');
  if (!focused) {
    return false;
  }
  return props.fileId === focused || props.url.includes(focused);
}

function makeFacadePoster(poster: string) {
  return h('img', {
    src: poster,
    alt: '',
    loading: 'lazy',
    decoding: 'async',
    class: 'absolute inset-0 h-full w-full object-cover',
  });
}

/**
 * A lightweight stand-in for the real Vidstack player: just the poster image
 * (or a neutral placeholder) plus a play button. Clicking it mounts the actual
 * player. This avoids instantiating a full player component per video on load.
 */
function renderVideoFacade(props: Props) {
  const aspectRatio = props.ratio ? `100 / ${props.ratio}` : '16 / 9';
  return h(
    'button',
    {
      type: 'button',
      'aria-label': 'Play video',
      class:
        'media-facade group relative block w-full cursor-pointer overflow-hidden rounded-md bg-gray-900',
      style: `aspect-ratio: ${aspectRatio};`,
    },
    props.poster && makeFacadePoster(props.poster),
    h(
      'span',
      { class: 'absolute inset-0 flex items-center justify-center' },
      h('span', {
        class:
          'flex items-center justify-center gap-2 rounded-full bg-gray-900/40 p-4 text-white transition-all group-hover:bg-gray-900/50',
        innerHTML: playIcon('size-10'),
      }),
    ),
  );
}

/**
 * The audio equivalent: the same two rows as the real player's control bar
 * (time track, then play button + clock) at the same heights, so activating
 * it doesn't shift the lesson content below.
 */
function renderAudioFacade() {
  return h(
    'button',
    {
      type: 'button',
      'aria-label': 'Play audio',
      class:
        'media-facade group block w-full cursor-pointer rounded-lg border bg-gray-50 text-left text-gray-600 dark:border-transparent dark:bg-gray-800 dark:text-gray-200',
    },
    h(
      'span',
      { class: 'flex h-10 items-center px-2' },
      h('span', { class: 'mx-[7.5px] h-[5px] w-full rounded-sm bg-gray-300 dark:bg-gray-700' }),
    ),
    h(
      'span',
      { class: '-mt-0.5 flex items-center gap-1 px-2 pb-2' },
      h('span', {
        class:
          'flex h-8 w-8 items-center justify-center rounded-md transition-colors group-hover:bg-gray-200 md:h-10 md:w-10 dark:group-hover:bg-gray-700',
        innerHTML: playIcon('size-6'),
      }),
      h('span', { class: 'ml-1.5 text-sm font-medium tabular-nums' }, '0:00'),
    ),
  );
}

function renderFacade(props: Props) {
  return isVideo(props.type) ? renderVideoFacade(props) : renderAudioFacade();
}

/**
 * If we're serializing, we'll output a standard audio / video
 * element and call it a day. If we're serializing as email,
 * we ought to output a poster image and a link that takes the
 * user to a special media page where the audio / video can
 * be consumed (that's still a TODO). If we're rendering for
 * edit / live view, we'll load the correct URL from the server
 * so that if it's been transcoded, we use that. While we're
 * loading, we render a placeholder w/ the correct aspect ratio.
 */

export function renderMediaPlayer(props: Props) {
  const wrapper = h('.grow');

  // Resolve the file once (to pick up the transcoded URL / poster), and share
  // that between the facade's poster lookup and the eventual player mount.
  let resolved: Promise<any> | undefined;
  function resolve(): Promise<any> {
    const isFileReady = props.isResolved || props.tmpUrl || isAbsoluteUrl(props.url);
    if (isFileReady) {
      return Promise.resolve(props);
    }
    if (!resolved) {
      resolved = ajax(props.url);
      // Don't cache a rejection: a transient failure would otherwise poison
      // every later attempt (poster prefetch and click-to-play). Clear it so
      // the next call retries.
      resolved.catch(() => {
        resolved = undefined;
      });
    }
    return resolved;
  }

  async function init(autoplay?: boolean) {
    const result = await resolve();
    const attrs: Omit<VidstackProps, 'autoplay'> & { autoplay?: string } = {
      id: result.id,
      url: props.tmpUrl || result.url,
      type: result.type,
      poster: props.poster || result.poster,
      load: props.load,
      overlay: props.overlay,
      ratio: props.ratio?.toString(),
      hideCaptions: props.hideCaptions,
      hasCaptions: !!(props.captionsURL || result.captionsURL),
      captionsURL: props.captionsURL || result.captionsURL,
      autoplay: autoplay ? 'true' : undefined,
    };

    const player = h(vidstackTagName, attrs);
    wrapper.append(player);

    // If we are in edit mode, and haven't yet computed an aspect ratio, we'll do so now.
    if (props.setProps) {
      player.addEventListener(
        'can-play',
        () => {
          const ratio = getAspectRatio(wrapper);
          if (ratio !== props.ratio) {
            props.setProps?.((x) => ({ ...x, ratio }));
          }
        },
        true,
      );

      const isModified = props.fileId !== attrs.id || props.hasCaptions !== attrs.hasCaptions;
      // If we're in edit mode, we'll update the captions URL when it becomes available.
      if (isModified) {
        props.setProps?.((x) => ({ ...x, hasCaptions: attrs.hasCaptions, fileId: attrs.id }));
      }
    }

    // If the caller wants the video element, we'll attempt to provide it.
    if (props.onVideoElement) {
      setTimeout(() => {
        const video = wrapper.querySelector('video');
        if (video) {
          props.onVideoElement?.(video);
        }
      }, 10);
    }
  }

  if (props.lazy && !isFocusedFile(props)) {
    // Defer mounting the real player until the viewer clicks the facade.
    const facade = renderFacade(props);
    // Cards don't always inline a poster, but the video usually has one on the
    // server. Fetch it so the facade isn't a blank placeholder.
    if (!props.poster && isVideo(props.type)) {
      resolve()
        .then((result) => {
          if (result.poster) {
            facade.insertBefore(makeFacadePoster(result.poster), facade.firstChild);
          }
        })
        .catch(() => {});
    }

    // Guard against a second activation (rapid clicks, or keyboard Enter/Space
    // which `pointer-events: none` doesn't block) mounting the player twice.
    let mounting = false;
    function mountFromFacade(autoplay?: boolean) {
      if (mounting) {
        return;
      }
      mounting = true;
      stopWatchingFocus();
      // Keep the facade in place (but inert) until the player is mounted so the
      // layout doesn't collapse.
      facade.style.pointerEvents = 'none';
      (facade as HTMLButtonElement).disabled = true;
      init(autoplay)
        .then(() => facade.remove())
        .catch((err) => {
          facade.remove();
          showMediaError(err, wrapper);
        });
    }

    // A live player reacts to `?focusedFile` changes (e.g. a transcript-search
    // jump to another video in the same, already-mounted lesson) via its
    // useFocusedFile hook. A facade can't, so watch navigation ourselves and
    // mount the real player when this video becomes the focus target — the
    // mounted player then scrolls/seeks/plays. `navigatesuccess` fires after the
    // URL commits, so isFocusedFile() reads the new params.
    function onNavigate() {
      if (!facade.isConnected) {
        // The lesson was left (its minidoc disposed); self-clean the listener.
        stopWatchingFocus();
        return;
      }
      if (isFocusedFile(props)) {
        mountFromFacade();
      }
    }
    function stopWatchingFocus() {
      window.navigation?.removeEventListener('navigatesuccess', onNavigate);
    }
    window.navigation?.addEventListener('navigatesuccess', onNavigate);

    facade.addEventListener('click', () => mountFromFacade(true));
    wrapper.append(facade);
  } else {
    // Display a little error UI if we fail to initialize
    init().catch((err) => showMediaError(err, wrapper));
  }

  return h(`.relative.rounded-lg`, wrapper);
}

export function setPoster({ poster, playerEl }: { poster: string; playerEl: HTMLElement }) {
  const component = playerEl.querySelector(vidstackTagName);
  const video = playerEl.querySelector('video');
  if (video) {
    video.poster = poster;
  }
  if (component) {
    (component as any).poster = poster;
  }
}
